import { Component, computed, inject, SecurityContext, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { GbTheme, THEME_SIGNAL } from '../../interfaces/theme';
import { FaqService } from '../../services/faq/faq.service';
import { SectionTagComponent } from '../section-tag/section-tag.component';

interface SanitizedFaqContent {
  _id?: number;
  question: SafeHtml;
  answer: SafeHtml;
}

@Component({
  selector: 'app-faq-preview',
  imports: [SectionTagComponent, RouterLink, TranslatePipe],
  templateUrl: './faq-preview.component.html',
  styleUrl: './faq-preview.component.scss',
})
export class FaqPreviewComponent {
  public faqService = inject(FaqService);

  public theme = inject(THEME_SIGNAL);

  public sanitizer = inject(DomSanitizer);

  public readonly GbTheme = GbTheme;

  // Track which accordion item is currently expanded
  public expandedIndex = signal<number | null>(0); // First item expanded by default

  public faqs = computed<SanitizedFaqContent[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const faqs = Object.values(this.faqService.faqContent())[0] || [];
    return faqs.map<SanitizedFaqContent>(({ question, answer, _id }) => {
      // First sanitize the HTML to remove dangerous content
      const sanitizedQuestion = this.sanitizer.sanitize(SecurityContext.HTML, question) || '';
      const sanitizedAnswer = this.sanitizer.sanitize(SecurityContext.HTML, answer) || '';

      // Then mark the sanitized content as safe for innerHTML binding
      return {
        _id,
        question: sanitizedQuestion as SafeHtml,
        answer: sanitizedAnswer as SafeHtml,
      };
    });
  });

  public toggleAccordion(index: number): void {
    const currentExpanded = this.expandedIndex();
    // If clicking the same item, close it; otherwise, open the clicked item
    this.expandedIndex.set(currentExpanded === index ? null : index);
  }

  public isExpanded(index: number): boolean {
    return this.expandedIndex() === index;
  }
}
